import { PayloadAction, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { ActionModule } from '@typesApp/ActionModule'
import { AutoNullifyStatus, ClaimRecord } from '@typesApp/ClaimRecord'
import { Customer } from '@typesApp/Session'
import { Price } from '@typesApp/Price'
import { HostEnvironment, HostSession, InitialHostEnvironment } from '@typesApp/Environment'
import { CartBundle, ProductBundle } from '@typesApp/Product'
import { RefreshCartPayload } from '@typesApp/ActionModule'
import { MyClaimsState } from '@typesApp/MyClaim'
import { ApiConfigurationError } from '@typesApp/Api'
import { AppErrorType } from '@typesApp/AppError'
import { ConnectorErrorType } from '@typesApp/Connector'
import { AppState } from '@redux/store'
import { authApi } from '@redux/queries/auth'
import { sessionsApi } from '@redux/queries/sessions'
import { clearLoginRedirectParam } from '@utils/navigationUtil'
import { isAnonymousSession } from '@utils/appInitUtil'
import { clearLocalAuthToken, setLocalAuthToken } from '@utils/authToken'

type PreviousSessionState = {
  userId?: string
  sessionId?: string
}

type ConnectorState = {
  error: ConnectorErrorType
  cartBundle?: CartBundle
  isOrderComplete: boolean
  environment: HostEnvironment
  session?: HostSession
  previousSession?: PreviousSessionState
  sessionCustomer?: Customer
  sessionExpired: boolean
  authTokenReady: boolean
  authTokenError?: boolean
  authTokenSessionId?: string
  isSessionTransitioned?: boolean
  isSessionInitializing?: boolean
  isLoggingOut: boolean
  actionModule?: ActionModule
  lastFormActionTime?: Date
  myClaims: MyClaimsState
  autoNullify?: AutoNullifyStatus
  isLoginEvent?: boolean
}

const DEFAULT_GUEST_SESSION: HostSession = {
  sessionId: '',
  isGuest: true,
  isLoggedIn: false,
  isAnonymous: false,
  isPartiallyAuthenticated: false,
}

const initialState: ConnectorState = {
  error: ConnectorErrorType.NONE,
  isOrderComplete: false,
  sessionExpired: false,
  authTokenReady: false,
  isLoggingOut: false,
  environment: InitialHostEnvironment,
  myClaims: {
    shoppingActions: [],
  },
}

export const connectorSlice = createSlice({
  name: 'connector',
  initialState: initialState,
  reducers: {
    setConnectorError(state, action: PayloadAction<ConnectorErrorType>) {
      state.error = action.payload
    },
    clearConnectorError(state) {
      state.error = ConnectorErrorType.NONE
    },
    connectorInitCartBundle(state, action: PayloadAction<CartBundle>) {
      state.isOrderComplete = false
      state.cartBundle = action.payload
    },
    connectorAfterCreateClaimRecord(state, action: PayloadAction<CartBundle | undefined>) {
      state.isOrderComplete = false
      state.cartBundle = action.payload
    },
    connectorSetSessionInitializing(state, action: PayloadAction<boolean>) {
      state.isSessionInitializing = action.payload
    },
    connectorInitAnonymousSession(state, action: PayloadAction<HostSession>) {
      const session = action.payload
      if (
        !session.sessionId &&
        !session?.cartId &&
        !session?.isGuest &&
        !session?.isLoggedIn &&
        !session?.isPartiallyAuthenticated
      ) {
        const { userId, ...rest } = action.payload
        state.session = { ...rest, isAnonymous: true }
      }
    },
    connectorInitSession(state, action: PayloadAction<HostSession>) {
      const prevSessionId = state.previousSession?.sessionId
      const currSessionId = action.payload?.sessionId

      // logging out, still same session
      if (state.isLoggingOut && !action.payload.isGuest && currSessionId && currSessionId === prevSessionId) {
        return
      }

      // reset session state on new session
      const isLoggedInOrGuest = action.payload.isLoggedIn || action.payload.isGuest
      const isGuestWithSession =
        !action.payload.isLoggedIn && !action.payload.isGuest && Boolean(action.payload.sessionId)
      const isSessionIdChanged =
        Boolean(action.payload.sessionId) && action.payload.sessionId !== state.session?.sessionId
      const isResetSession = isSessionIdChanged && (isLoggedInOrGuest || isGuestWithSession)

      if (isResetSession) {
        if (state.isLoggingOut) {
          state.isLoggingOut = false
        }
        if (state.sessionExpired) {
          state.sessionExpired = false
        }

        state.previousSession = undefined
      }

      // store userId in state.sessionCustomer
      const { userId, ...rest } = action.payload
      state.session = { ...state.session, ...rest, userId }

      if (state.sessionCustomer) {
        state.sessionCustomer.id = userId ?? ''
      } else if (userId) {
        state.sessionCustomer = {
          id: userId,
        }
      }

      if ((isLoggedInOrGuest || isGuestWithSession) && action.payload.sessionId) {
        clearLoginRedirectParam()
      }
    },
    connectorInitGuestLogin(state) {
      // after authenticated user logs out, the isLoggingOut flag remains true
      // if guest session is started immediately, the isLoggingOut = true causes
      // the getCustomerContext hook to return an invalid result. Hence we need
      // to reset some state on guest session login.
      state.isLoggingOut = false
      state.sessionExpired = false
      state.previousSession = undefined
    },
    connectorInitGuestSession(state, action: PayloadAction<{ userId?: string; cartId?: string; sessionId?: string }>) {
      if (!state.session) {
        state.session = {
          ...DEFAULT_GUEST_SESSION,
        }
      }

      if (action.payload) {
        const { userId, cartId, sessionId } = action.payload

        // commerce F/E may not send userId, cartId, sessionId
        // all at once so we must not overwrite current values

        if (userId) {
          // update customer number
          state.sessionCustomer = {
            ...state.sessionCustomer,
            id: userId,
          }

          state.session.userId = userId
        }

        if (cartId) state.session.cartId = cartId
        if (sessionId) state.session.sessionId = sessionId

        state.session.isGuest = true
        state.session.isLoggedIn = false
        state.session.isAnonymous = false
        state.session.isPartiallyAuthenticated = false
      }
    },
    connectorInitEnvironment(state, action: PayloadAction<HostEnvironment>) {
      state.environment = action.payload
    },
    connectorInitActionModule(state, action: PayloadAction<ActionModule>) {
      state.actionModule = action.payload
    },
    connectorInitMyClaims(state, action: PayloadAction<MyClaimsState>) {
      state.myClaims = action.payload
    },
    connectorRefreshCart(state, action: PayloadAction<ClaimRecord | undefined>) {
      // payload used only by simulator
      const payload = {
        claimAmount: action.payload?.claimData?.claim?.confirmedAmount?.amount,
        residualAmount:
          (action.payload?.orderSubtotal?.amount ?? 0) -
          (action.payload?.claimData?.claim?.confirmedAmount?.amount ?? 0),
      } as RefreshCartPayload

      state.actionModule?.refreshCart(payload)
    },
    connectorLogoutInProgress(state) {
      clearLocalAuthToken()
      return {
        ...initialState,
        isLoggingOut: true,
        previousSession: {
          userId: state.sessionCustomer?.id,
          sessionId: state.session?.sessionId,
        },
      }
    },
    connectorOrderComplete(state) {
      state.isOrderComplete = true
      state.cartBundle = undefined
    },
    connectorLastFormActionTime(state) {
      state.lastFormActionTime = new Date()
    },
    sessionExpired(state) {
      clearLocalAuthToken()

      state.sessionExpired = true
      state.authTokenReady = false
      state.isSessionInitializing = false

      state.previousSession = {
        userId: state.sessionCustomer?.id,
        sessionId: state.session?.sessionId,
      }

      // reset current session customer details
      state.sessionCustomer = undefined
    },
    resetSessionExpired(state) {
      state.sessionExpired = false
    },
    sessionTransitioned(state) {
      state.previousSession = {
        userId: state.sessionCustomer?.id,
        sessionId: state.session?.sessionId,
      }

      state.isSessionTransitioned = true

      // reset current session customer, so that transition from
      // authenticated user to guest user will not retain the customer details
      state.sessionCustomer = undefined
    },
    resetSessionTransitioned(state) {
      state.isSessionTransitioned = undefined
    },
    authTokenReady(state, action: PayloadAction<{ authTokenSessionId: string }>) {
      state.authTokenReady = true
      state.authTokenSessionId = action.payload.authTokenSessionId
    },
    authTokenReset(state) {
      state.authTokenReady = false
      state.authTokenSessionId = undefined
      clearLocalAuthToken()
    },
    authTokenError(state) {
      state.authTokenError = true
    },
    connectorAutoNullifyStatus(state, action: PayloadAction<AutoNullifyStatus>) {
      state.autoNullify = {
        ...state.autoNullify,
        ...action.payload,
      }
    },
    connectorOnLogin(state) {
      state.isLoginEvent = true
    },
    connectorResetOnLoginEvent(state) {
      state.isLoginEvent = undefined
    },
    resetConnectorInitialState(state) {
      return {
        ...initialState,
        previousSession: {
          userId: state.sessionCustomer?.id,
          sessionId: state.session?.sessionId,
        },
      }
    },
  },
  extraReducers: builder => {
    /**
     * Cache local auth token on new token fetch to ensure subsequent requests
     * have access to the token
     */
    builder.addMatcher(authApi.endpoints.getAuthToken.matchFulfilled, (state, action) => {
      setLocalAuthToken(action.payload)
    })

    builder.addMatcher(sessionsApi.endpoints.getSession.matchFulfilled, (state, action) => {
      state.sessionCustomer = action.payload.customer
    })

    /**
     * Update session id if customer context is refetched, e.g. on partial authenticated state
     * and we still have the old session id
     */
    builder.addMatcher(sessionsApi.endpoints.getCustomerContext.matchFulfilled, (state, action) => {
      const context = action.payload
      if (state.session && state.session.sessionId !== context?.sessionId) {
        state.session.sessionId = context.sessionId ?? ''
        state.session.isPartiallyAuthenticated = Boolean(context.isPartiallyAuthenticated)
      }
    })

    /**
     * API configuration error
     */
    builder.addMatcher(
      isAnyOf(sessionsApi.endpoints.getSession.matchRejected, sessionsApi.endpoints.getCustomerContext.matchRejected),
      (state, action) => {
        const data = action.payload?.data as ApiConfigurationError
        if (data?.errorType === AppErrorType.BAD_CONFIGURATION) {
          state.error = ConnectorErrorType.CONFIGURATION_ERROR
          console.error(data?.details)
        }
      }
    )
  },
})

export const {
  setConnectorError,
  clearConnectorError,
  connectorInitCartBundle,
  connectorAfterCreateClaimRecord,
  connectorSetSessionInitializing,
  connectorInitSession,
  connectorInitGuestLogin,
  connectorInitGuestSession,
  connectorInitAnonymousSession,
  connectorInitEnvironment,
  connectorInitActionModule,
  connectorInitMyClaims,
  connectorRefreshCart,
  connectorLogoutInProgress,
  connectorLastFormActionTime,
  connectorOrderComplete,
  sessionExpired,
  resetSessionExpired,
  sessionTransitioned,
  resetSessionTransitioned,
  authTokenReady,
  authTokenReset,
  authTokenError,
  connectorOnLogin,
  connectorResetOnLoginEvent,
  connectorAutoNullifyStatus,
  resetConnectorInitialState,
} = connectorSlice.actions

export const connectorErrorSelector = (state: AppState): ConnectorErrorType => state.connector.error
export const connectorCartProductsSelector = (state: AppState): ProductBundle[] | undefined =>
  state.connector.cartBundle?.products
export const connectorOrderShippingCostsSelector = (state: AppState): Price | undefined =>
  state.connector.cartBundle?.shippingCosts
export const connectorOrderCompleteSelector = (state: AppState): boolean => state.connector.isOrderComplete === true
export const connectorSessionSelector = (state: AppState): HostSession | undefined => state.connector.session
export const connectorSessionIdSelector = (state: AppState): string => state.connector.session?.sessionId ?? ''
export const connectorSessionCustomerSelector = (state: AppState): Customer | undefined =>
  state.connector.sessionCustomer
export const connectorSessionCallableSelector = (state: AppState): boolean => Boolean(state.connector.actionModule)
export const connectorEnvironmentSelector = (state: AppState): HostEnvironment => state.connector.environment
export const connectorActionModuleSelector = (state: AppState): ActionModule | undefined => state.connector.actionModule
export const connectorLastFormActionTimeSelector = (state: AppState): Date | undefined =>
  state.connector.lastFormActionTime
export const connectorMyClaimsSelector = (state: AppState): MyClaimsState => state.connector.myClaims
export const connectorConfigurationErrorSelector = (state: AppState): boolean =>
  state.connector.error === ConnectorErrorType.CONFIGURATION_ERROR
export const isSessionInitializingSelector = (state: AppState): boolean =>
  state.connector.isSessionInitializing === true
export const sessionExpiredSelector = (state: AppState): boolean => state.connector.sessionExpired
export const sessionReadySelector = (state: AppState): boolean =>
  state.connector.authTokenReady && Boolean(state.connector.sessionCustomer)
export const authTokenReadySelector = (state: AppState): boolean => state.connector.authTokenReady
export const authTokenSessionIdSelector = (state: AppState): string | undefined => state.connector.authTokenSessionId
export const authTokenErrorSelector = (state: AppState): boolean => state.connector.authTokenError === true
export const isGuestUserSelector = (state: AppState): boolean => state.connector.session?.isGuest === true
export const isAnonymousUserSelector = (state: AppState): boolean => isAnonymousSession(state.connector.session)
export const loggedInSelector = (state: AppState): boolean => state.connector.session?.isLoggedIn === true
export const loggingOutSelector = (state: AppState): boolean => state.connector.isLoggingOut
export const partiallyAuthenticatedSelector = (state: AppState): boolean =>
  state.connector.session?.isPartiallyAuthenticated === true
export const authenticatedSelector = (state: AppState): boolean =>
  state.connector.session?.isLoggedIn === true || state.connector.session?.isPartiallyAuthenticated === true
export const connectorAutoNullifyStatusSelector = (state: AppState): AutoNullifyStatus | undefined =>
  state.connector.autoNullify
